import {
  hasAsbSuiteFreeTrial,
  hasBasicReturns,
  hasManagedReturns,
  hasPaidAsbSuite,
  hasPaidAvaTax,
  isAvalaraIncludedCustomer,
} from '@avalara/bat-apps-shared/helpers/obligations';

import type { MarketingData } from '@avalara/types/marketing';
import type { Obligation } from '@avalara/types/ztb';

import type { Currency } from '@avalara/types/currency';
import type { ActiveFlow, BiztechMarketingContext, ReturnsEligibilityStatus } from '@avalara/types/index';

export const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function truthy<T>(value: T | null | undefined): boolean {
  if (value == null) return false;
  switch (typeof value) {
    case 'number':
      // Override default 0 as falsy behavior, but maintain for NaN
      return !Number.isNaN(value);
    default:
      return Boolean(value);
  }
}

// Andrew - Number and boolean types have legitimate falsy values that we want
//          to use, such as false or 0. For empty string values (and anything
//          else) we want to default to the specified backup.
export function withDefault<T>(value: T | null | undefined, defaultValue: T): T {
  if (value == null) return defaultValue;
  switch (typeof value) {
    case 'number':
    case 'boolean':
      return value;
    default:
      return value || defaultValue;
  }
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export function onError(e: any, fallbackImg: string) {
  e.target.onerror = null;
  e.target.src = fallbackImg;
}

export function getSearchParam(param: string): string | null {
  try {
    const searchParams = window.location.href.slice(window.location.href.indexOf('?'));
    return new URLSearchParams(searchParams).get(param);
  } catch {
    // do nothing
  }
  return null;
}

export const isMobileDevice = () =>
  !!(
    navigator.userAgent.match(/Android/i) ||
    navigator.userAgent.match(/webOS/i) ||
    navigator.userAgent.match(/iPhone/i) ||
    navigator.userAgent.match(/iPad/i) ||
    navigator.userAgent.match(/iPod/i) ||
    navigator.userAgent.match(/BlackBerry/i) ||
    navigator.userAgent.match(/Windows Phone/i)
  );

const small = '(and|as|at|but|by|for|if|in|of|on|or|to)';
const large = '(us)';

const lower = (word: string): string => word.toLowerCase();

export const capitalize = (word: string): string => `${word.substr(0, 1).toUpperCase()}${word.substr(1).toLowerCase()}`;

const upper = (word: string): string => word.toUpperCase();

export const toProperCase = (name: string): string => {
  const parts = [];
  const split = /\s/g;
  let index = 0;

  // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
  let currentSplitExecState;
  while (currentSplitExecState !== null) {
    currentSplitExecState = split.exec(name);

    parts.push(
      name
        .substring(index, currentSplitExecState ? currentSplitExecState.index : name.length)
        .replace(/\b([A-Za-z]*)\b/g, (match) => capitalize(match))
        .replace(RegExp(`\\b${small}\\b`, 'ig'), (match) => lower(match))
        .replace(RegExp(`\\b${large}\\b`, 'ig'), (match) => upper(match)),
    );

    index = split.lastIndex;

    if (currentSplitExecState !== null) parts.push(currentSplitExecState[0]);
  }

  return parts.join('');
};

export const getSalestaxDefaultText = (salestax: number | null) => {
  if (Boolean(salestax) || salestax === 0) {
    return '';
  }

  return ' + any applicable taxes or fees';
};

const defaultMarketingData = {
  lsmr: 'Direct Visitor',
  lso: 'Direct Visitor',
  salesRepId: '',
};

export const getBiztechMarketingContext = (
  // biome-ignore lint/style/useDefaultParameterLast: legacy code -- used in too many places to change
  md: MarketingData = defaultMarketingData,
  activeFlow: ActiveFlow,
  connectorId = '',
): BiztechMarketingContext => {
  let marketingContext: BiztechMarketingContext = {};
  // We have special marketing context if connector is woocommerce (a0n3300000FSK2zAAH) for avatax-buy and avatax-direct-buy
  const avataxProductOfInterest = connectorId === 'a0n3300000FSK2zAAH' ? 'a1m0b000001SbLuAAK' : 'AvaTax';

  switch (activeFlow) {
    case 'avatax-buy':
    case 'avatax-direct-buy':
      marketingContext = { productsOfInterest: avataxProductOfInterest, webProperty: 'buy.avalara.com' };
      break;
    // TODO (Andrew) - do we need to add any specific fields for MRS or Basic returns here?
    default:
      break;
  }

  // If a Campaign ID was passed from a query param or cookie, use it to update the opportunity in SalesForce
  if (md.campaignIdFromParamsOrCookie) {
    marketingContext.campaignId = md.campaignIdFromParamsOrCookie;
  }
  if (md.partnerSource !== '' && md.partnerSource != null) {
    marketingContext.partnerId = md.partnerSource;
  }
  if (md.lso !== '' && md.lso != null) {
    marketingContext.leadSource = md.lso;
  } else {
    marketingContext.leadSource = 'Direct Visitor';
  }

  if (md.lsmr !== '' && md.lsmr != null) {
    marketingContext.leadSourceMostRecent = md.lsmr;
  } else {
    marketingContext.leadSourceMostRecent = 'Direct Visitor';
  }

  return marketingContext;
};

export const getReturnsEligibilityStatus = (obligations: Array<Obligation>): ReturnsEligibilityStatus => {
  const hasRsb = hasBasicReturns(obligations);
  const hasMrs = hasManagedReturns(obligations);

  if (hasRsb) return 'ALREADY_HAS_BASIC_RETURNS';
  if (!hasPaidAvaTax(obligations) && !isAvalaraIncludedCustomer(obligations)) {
    return 'NO_PERMANENT_AVATAX';
  }
  if (hasMrs) return 'ALREADY_HAS_RETURNS';

  return 'ELIGIBLE';
};

export const removeAvalaraPrefix = (offerName: string): string => offerName.replace(/^Avalara /, '');

export const formatPrice = (value: number, decimalPlaces = 2, currency: Currency = 'USD') => {
  const locale = (() => {
    switch (currency) {
      case 'GBP':
        return 'en-GB';
      case 'EUR':
        return 'en-EU';
      // case 'USD': same as default
      default:
        return 'en-US';
    }
  })();

  return value.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

export const hasAsbOrAsbTrial = (obligations: Array<Obligation>): boolean =>
  hasPaidAsbSuite(obligations) || hasAsbSuiteFreeTrial(obligations);

export const hasAccountsPayable = (obligations: Array<Obligation>): boolean =>
  obligations.some((o) => o.Id === 'aLZVG00000068ZC4AY' || o.productCatalogId === 'CALC_AVTX_AP_SVC');
